import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import { useRouter } from 'expo-router';

import ActionButton from 'components/action-button';
import AccountLayout from 'components/layout/drawer-page';
import { toaster } from 'components/toaster/toaster';
import { UserInfoForm } from 'components/user-info-form';
import type { UserInfoFormSubmitButtonState } from 'components/user-info-form/types';
import {
  CommunicationPreferenceType,
  useUpdateUserCommunicationPreferencesMutation,
} from 'generated/graphql-gateway';
import useEffectOnUnmount from 'hooks/use-effect-on-unmount';
import { useAuthContext } from 'state/auth';
import { ModalAuthScreen, ModalAuthTrigger, UserInfo } from 'state/auth/types';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyUserState } from 'state/loyalty/hooks/use-loyalty-user-state';
import { useUIContext } from 'state/ui';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';
import { routes } from 'utils/routing';

import { SMS_SUBSCRIPTION_EXP_TIME_MIN } from './comm-preferences-v2/comm-card-email-push';
import { signOutForReauthentication } from './utils/sign-out-for-reauthentication';

export const AccountInfo: FC<React.PropsWithChildren<unknown>> = () => {
  const smsSubscriptionEnabled = useFlag(LaunchDarklyFlag.ENABLE_SMS_MARKETING_SUBSCRIPTIONS);
  const [
    updateUserCommunicationPreferencesMutation,
  ] = useUpdateUserCommunicationPreferencesMutation();
  const { navigate } = useRouter();
  const { setUserEmail } = useUIContext();

  useLoyaltyUserState({ refreshOnMountOrFocus: true });

  const {
    closeModalAuth,
    setModalAuthState,
    signOut,
    user,
    updateUserInfo,
    setIsReAuthenticating,
    useUpdateMeMutationLoading,
  } = useAuthContext();

  const userInfo = useRef<UserInfo | null>(null);
  const submitUserInfoRef = useRef<UserInfoFormSubmitButtonState['submitForm'] | null>(null);
  const [localUser, setLocalUser] = useState(user);
  const [submitButtonState, setSubmitButtonState] = useState({
    disabled: true,
    isLoading: false,
  });

  const cleanup = useCallback(() => {
    setIsReAuthenticating(false);
    closeModalAuth();
  }, [closeModalAuth, setIsReAuthenticating]);

  useEffect(() => {
    if (user) {
      // Account for the scenario where the user attempts to access this component
      // without being authenticated, then authenticates and is redirected here.
      setLocalUser(user);
    }
  }, [user]);

  useEffectOnUnmount(() => {
    cleanup();
  });

  const handleOnUpdateUserSuccess = useCallback(() => {
    setIsReAuthenticating(false);
    navigate(routes.accountInfo);

    toaster.positive(t('successfullyUpdatedUserAccountInfo'));

    cleanup();
  }, [setIsReAuthenticating, navigate, cleanup]);

  const updateUser = useCallback(async () => {
    if (!userInfo.current) {
      return;
    }

    try {
      // Unsubscribe from SMS notifications when the phone number is updated
      const shouldUnsubscribeFromSMSNotifications =
        !!localUser?.details?.phoneNumber &&
        localUser.details.phoneNumber !== userInfo?.current.phoneNumber &&
        smsSubscriptionEnabled;

      if (shouldUnsubscribeFromSMSNotifications) {
        updateUserCommunicationPreferencesMutation({
          variables: {
            input: {
              communicationPreferenceType: CommunicationPreferenceType.MARKETINGSMS,
              newStatus: false,
            },
          },
        });

        syncStorage.setObjectWithExpiration(
          SyncStorageKey.MARKETING_SMS_SUBSCRIPTION_REQUESTED,
          false,
          SMS_SUBSCRIPTION_EXP_TIME_MIN
        );
      }

      // TODO: Merge preference and details update into a single request
      // once we merge to apollo
      const updateUserOptions = { shouldThrowException: true };

      await updateUserInfo(userInfo.current, updateUserOptions);

      handleOnUpdateUserSuccess();
    } catch {
      setIsReAuthenticating(false);
    }
  }, [
    localUser,
    smsSubscriptionEnabled,
    updateUserInfo,
    handleOnUpdateUserSuccess,
    updateUserCommunicationPreferencesMutation,
    setIsReAuthenticating,
  ]);

  const signOutGlobally = useCallback(
    () =>
      signOutForReauthentication({
        setIsReAuthenticating,
        signOut,
        source: 'Account Info',
      }),
    [signOut, setIsReAuthenticating]
  );

  const showModalAuth = useCallback(
    // This function is async as it needs tu return a Promise
    // as required by UserInfoForm's onSubmit prop.
    async (infoToUpdate: UserInfo) => {
      userInfo.current = infoToUpdate;

      setUserEmail(localUser?.details.email ?? ''); // Pre-populates user email in the sign-in form.

      setModalAuthState({
        screen: ModalAuthScreen.SIGN_IN,
        trigger: ModalAuthTrigger.Global,
        disableEmailInput: true,
        heading: t('pleaseReAuthenticateToContinue'),
        onPreSignIn: signOutGlobally,
        onSignInSuccess: updateUser,
        shouldDisplaySignInSuccessMessage: false,
        shouldSkipDefaultSignInCallback: true,
      });
    },
    [setUserEmail, localUser?.details.email, setModalAuthState, signOutGlobally, updateUser]
  );

  const handleSubmitButtonStateChange = useCallback(
    ({ disabled, isLoading, submitForm }: UserInfoFormSubmitButtonState) => {
      submitUserInfoRef.current = submitForm;
      setSubmitButtonState(currentState =>
        currentState.disabled === disabled && currentState.isLoading === isLoading
          ? currentState
          : { disabled, isLoading }
      );
    },
    []
  );

  const handleFooterSubmit = useCallback(() => {
    submitUserInfoRef.current?.();
  }, []);

  return (
    <AccountLayout
      headerText={t('account')}
      helmetTitle={t('accountDetails')}
      keyboardAwareFooter
      footer={
        <ActionButton
          testID="account-info-form-submit-button"
          fullWidth
          onPress={handleFooterSubmit}
          disabled={submitButtonState.disabled}
          isLoading={submitButtonState.isLoading}
        >
          {t('update')}
        </ActionButton>
      }
    >
      <UserInfoForm
        user={localUser}
        onSubmit={showModalAuth}
        readOnly={useUpdateMeMutationLoading}
        hideSubmitButton
        onSubmitButtonStateChange={handleSubmitButtonStateChange}
      />
    </AccountLayout>
  );
};
